import { useEffect, useMemo, useState } from "react";
import type { CompanyDetails } from "../../shared/types";
import { apiCompany, pushRecent } from "../lib/api";
import { formatCurrency, sentenceCase } from "../lib/format";
import { navigate } from "../lib/useHashRoute";
import { StatusBadge } from "./StatusBadge";
import { MonitorButton } from "./MonitorButton";
import { IconAlert, IconBuilding, IconCheck, IconCopy, IconTeiaMark, IconUser, IconWeb } from "./Icons";

type TimelineEvent = { date: string; sortKey: number; title: string; detail?: string };

function parseBrDate(value?: string): number {
  if (!value) return 0;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value);
  if (!match) return 0;
  return Number(`${match[3]}${match[2]}${match[1]}`);
}

function buildTimeline(details: CompanyDetails): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  if (details.openedAt) {
    events.push({ date: details.openedAt, sortKey: parseBrDate(details.openedAt), title: "Início das atividades" });
  }
  for (const partner of details.partners) {
    if (partner.joinedAt) {
      events.push({
        date: partner.joinedAt,
        sortKey: parseBrDate(partner.joinedAt),
        title: `Entrada no quadro societário`,
        detail: `${partner.name} · ${partner.role}`,
      });
    }
  }
  // Só entra na linha do tempo quando a empresa é optante de fato e a data existe;
  // algumas fontes mandam data zerada para quem nunca optou.
  if (details.taxRegime?.simples === true && details.taxRegime.simplesSince && parseBrDate(details.taxRegime.simplesSince) > 0) {
    events.push({ date: details.taxRegime.simplesSince, sortKey: parseBrDate(details.taxRegime.simplesSince), title: "Opção pelo Simples Nacional" });
  }
  if (details.taxRegime?.mei === true && details.taxRegime.meiSince && parseBrDate(details.taxRegime.meiSince) > 0) {
    events.push({ date: details.taxRegime.meiSince, sortKey: parseBrDate(details.taxRegime.meiSince), title: "Enquadramento como MEI" });
  }
  if (details.statusDate) {
    events.push({
      date: details.statusDate,
      sortKey: parseBrDate(details.statusDate),
      title: `Situação cadastral: ${details.status}`,
      detail: details.statusReason,
    });
  }
  if (details.specialSituationDate && details.specialSituation) {
    events.push({
      date: details.specialSituationDate,
      sortKey: parseBrDate(details.specialSituationDate),
      title: `Situação especial: ${details.specialSituation}`,
    });
  }
  return events.sort((a, b) => b.sortKey - a.sortKey);
}

/** Lista longa com "Mostrar mais": evita a parede de texto sem esconder dado.
 *  Listas que já cabem inteiras não ganham botão nenhum. */
type Paged<T> = { shown: T[]; remaining: number; expanded: boolean; paginated: boolean; toggle: () => void };

function useVisible<T>(items: T[], step: number): Paged<T> {
  const [visible, setVisible] = useState(step);
  const shown = items.slice(0, visible);
  const expanded = visible >= items.length;
  return {
    shown,
    remaining: items.length - shown.length,
    expanded,
    paginated: items.length > step,
    toggle: () => setVisible((count) => (expanded ? step : count + step)),
  };
}

function MoreButton<T>({ list, noun }: { list: Paged<T>; noun: string }) {
  if (!list.paginated) return null;
  return (
    <button className="btn btn-secondary btn-sm load-more" onClick={list.toggle}>
      {list.expanded ? "Mostrar menos" : `Mostrar mais ${list.remaining} ${noun}`}
    </button>
  );
}

export function CompanyPage({ cnpj }: { cnpj: string }) {
  const [details, setDetails] = useState<CompanyDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setDetails(null);
    setError(null);
    apiCompany(cnpj, controller.signal)
      .then((payload) => {
        setDetails(payload);
        pushRecent({ cnpj, name: payload.tradeName || payload.corporateName });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Não foi possível carregar a ficha.");
      });
    return () => controller.abort();
  }, [cnpj]);

  const timeline = useMemo(() => (details ? buildTimeline(details) : []), [details]);
  const partnersList = useVisible(details?.partners ?? [], 6);
  const branchesList = useVisible(details?.establishments ?? [], 6);
  const eventsList = useVisible(timeline, 8);
  const activitiesList = useVisible(details?.secondaryActivities ?? [], 6);
  const mapQuery = useMemo(() => {
    if (!details) return "";
    const parts = [
      [details.address.street, details.address.number].filter(Boolean).join(" "),
      details.address.neighborhood,
      details.address.city,
      details.address.state,
      details.address.zipCode,
    ].filter(Boolean);
    return parts.length >= 3 ? parts.join(", ") : "";
  }, [details]);

  if (error) {
    return (
      <main className="page container">
        <div className="state-box">
          <IconAlert size={32} />
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate({ page: "home" })}>
            Voltar para a busca
          </button>
        </div>
      </main>
    );
  }

  if (!details) {
    return (
      <main className="page container">
        <div className="state-box loading">
          <span className="spinner" />
          <p>Consultando fontes públicas…</p>
        </div>
      </main>
    );
  }

  const addressLine = [
    [details.address.street, details.address.number].filter(Boolean).join(", "),
    details.address.complement,
    details.address.neighborhood,
  ]
    .filter(Boolean)
    .join(" · ");

  const copyCnpj = () => {
    navigator.clipboard?.writeText(details.cnpj).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => undefined);
  };

  return (
    <main className="page container">
      {details.demo && (
        <div className="demo-banner">
          <IconAlert size={16} />
          <span>Modo demonstração: as fontes públicas não responderam, exibindo um snapshot local.</span>
        </div>
      )}

      <div className="page-head">
        <div className="page-head-main">
          <h1 className="page-title">{details.corporateName}</h1>
          {details.tradeName && details.tradeName !== details.corporateName && (
            <p className="page-subtitle">{details.tradeName}</p>
          )}
          <div className="page-badges">
            <span className="badge plain mono">{details.cnpj}</span>
            <StatusBadge status={details.status} />
            {details.branchType && <span className="badge plain">{details.branchType}</span>}
            {details.taxRegime?.simples && <span className="badge plain">Simples Nacional</span>}
            {details.taxRegime?.mei && <span className="badge plain">MEI</span>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => navigate({ page: "network", cnpj })}>
            <IconWeb size={16} /> Abrir teia
          </button>
          <MonitorButton cnpj={cnpj} />
          <button className="btn btn-secondary" onClick={copyCnpj}>
            {copied ? <><IconCheck size={15} /> Copiado</> : <><IconCopy size={15} /> Copiar CNPJ</>}
          </button>
        </div>
      </div>

      <div className="summary-strip">
        <div className="summary-item">
          <span className="summary-label">Abertura</span>
          <span className="summary-value">{details.openedAt ?? "Não informado"}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Capital social</span>
          <span className="summary-value">{formatCurrency(details.capital)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Cidade / UF</span>
          <span className="summary-value">
            {[details.address.city, details.address.state].filter(Boolean).join(" / ") || "Não informado"}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Sócios publicados</span>
          <span className="summary-value">{details.partners.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Estabelecimentos</span>
          <span className="summary-value">{Math.max(details.establishments.length, 1)}</span>
        </div>
      </div>

      <div className="card-grid">
        <section className="card span-2">
          <h2>Quadro societário ({details.partners.length})</h2>
          <div className="partner-list">
            {details.partners.length === 0 && (
              <div className="empty-explain">
                <p>{details.partnersNote ?? "Nenhum sócio publicado para este CNPJ."}</p>
                {details.owner && (
                  <button
                    className="partner-row clickable"
                    onClick={() => navigate({ page: "person", name: details.owner!.name, doc: details.owner!.maskedDocument, de: details.cnpj })}
                    title={`Abrir o perfil de ${details.owner.name}`}
                  >
                    <span aria-hidden><IconUser size={17} /></span>
                    <span className="partner-info">
                      <span className="partner-name">{details.owner.name}</span>
                      <span className="partner-meta">
                        <span>Titular</span>
                        {details.owner.maskedDocument && <span className="mono">{details.owner.maskedDocument}</span>}
                        <span>{details.owner.basis}</span>
                      </span>
                    </span>
                    <span className="partner-go" aria-hidden>Perfil →</span>
                  </button>
                )}
                {details.matrizCnpj && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate({ page: "company", cnpj: details.matrizCnpj!.replace(/\D/g, "") })}
                  >
                    <IconBuilding size={15} /> Ver o quadro societário da matriz
                  </button>
                )}
              </div>
            )}
            {partnersList.shown.map((partner) => (
              <button
                className="partner-row clickable"
                key={partner.id}
                onClick={() => navigate(partner.legalEntityCnpj
                  ? { page: "company", cnpj: partner.legalEntityCnpj.replace(/\D/g, "") }
                  : { page: "person", name: partner.name, doc: partner.maskedDocument, de: details.cnpj })}
                title={partner.legalEntityCnpj ? `Abrir a ficha de ${partner.name}` : `Abrir o perfil de ${partner.name}`}
              >
                <span aria-hidden>{partner.type === "Pessoa Jurídica" ? <IconBuilding size={17} /> : <IconUser size={17} />}</span>
                <span className="partner-info">
                  <span className="partner-name">{partner.name}</span>
                  <span className="partner-meta">
                    <span>{partner.role}</span>
                    {partner.maskedDocument && <span className="mono">{partner.maskedDocument}</span>}
                    {partner.joinedAt && <span>Desde {partner.joinedAt}</span>}
                    {partner.ageGroup && <span>{partner.ageGroup}</span>}
                    {partner.country && partner.country !== "Brasil" && <span>{partner.country}</span>}
                  </span>
                  {partner.representativeName && (
                    <span className="partner-meta">
                      <span>Representante legal: {partner.representativeName}{partner.representativeRole ? ` (${partner.representativeRole})` : ""}</span>
                    </span>
                  )}
                </span>
                <span className="partner-go" aria-hidden>{partner.legalEntityCnpj ? "Ficha →" : "Perfil →"}</span>
              </button>
            ))}
            <MoreButton list={partnersList} noun="sócios" />
          </div>
        </section>

        <section className="card">
          <h2>Identificação</h2>
          <dl className="field-list">
            <div className="field wide"><dt>Razão social</dt><dd>{details.corporateName}</dd></div>
            {details.tradeName && <div className="field"><dt>Nome fantasia</dt><dd>{details.tradeName}</dd></div>}
            {details.openedAt && <div className="field"><dt>Abertura</dt><dd>{details.openedAt}</dd></div>}
            {details.sector && (
              <div className="field wide">
                <dt>Setor (CNAE)</dt>
                <dd>{sentenceCase(details.sector.name)} <span className="badge plain mono">{details.sector.code}</span></dd>
              </div>
            )}
            {details.legalNature && <div className="field wide"><dt>Natureza jurídica</dt><dd>{sentenceCase(details.legalNature)}</dd></div>}
            {details.size && <div className="field"><dt>Porte</dt><dd>{sentenceCase(details.size)}</dd></div>}
            <div className="field"><dt>Capital social</dt><dd>{formatCurrency(details.capital)}</dd></div>
            {details.branchType && <div className="field"><dt>Tipo de unidade</dt><dd>{sentenceCase(details.branchType)}</dd></div>}
            {details.federativeEntity && <div className="field"><dt>Ente federativo</dt><dd>{details.federativeEntity}</dd></div>}
          </dl>
        </section>

        <section className="card">
          <h2>Situação e regime</h2>
          <dl className="field-list">
            <div className="field">
              <dt>Situação cadastral</dt>
              <dd>
                {details.status}
                {details.statusDate ? ` desde ${details.statusDate}` : ""}
              </dd>
            </div>
            {details.statusReason && <div className="field"><dt>Motivo</dt><dd>{sentenceCase(details.statusReason)}</dd></div>}
            {details.specialSituation && (
              <div className="field wide">
                <dt>Situação especial</dt>
                <dd>{sentenceCase(details.specialSituation)}{details.specialSituationDate ? ` desde ${details.specialSituationDate}` : ""}</dd>
              </div>
            )}
            <div className="field">
              <dt>Simples Nacional</dt>
              <dd>
                {details.taxRegime?.simples === true ? `Optante${details.taxRegime.simplesSince ? ` desde ${details.taxRegime.simplesSince}` : ""}` : details.taxRegime?.simples === false ? "Não optante" : "Sem informação"}
              </dd>
            </div>
            <div className="field">
              <dt>MEI</dt>
              <dd>
                {details.taxRegime?.mei === true ? `Enquadrada${details.taxRegime.meiSince ? ` desde ${details.taxRegime.meiSince}` : ""}` : details.taxRegime?.mei === false ? "Não enquadrada" : "Sem informação"}
              </dd>
            </div>
          </dl>
        </section>

        <section className="card">
          <h2>Endereço e contato</h2>
          <dl className="field-list">
            <div className="field wide"><dt>Endereço</dt><dd>{addressLine || "Não informado"}</dd></div>
            <div className="field">
              <dt>Cidade / UF</dt>
              <dd>{[details.address.city, details.address.state].filter(Boolean).join(" / ") || "Não informado"}</dd>
            </div>
            <div className="field"><dt>CEP</dt><dd className="mono">{details.address.zipCode || "Não informado"}</dd></div>
            <div className="field">
              <dt>Telefones</dt>
              <dd>{details.contact.phones.length ? details.contact.phones.join(" · ") : "Não informado"}</dd>
            </div>
            <div className="field wide"><dt>E-mail</dt><dd className={details.contact.email ? "mono" : undefined}>{details.contact.email ?? "Não informado"}</dd></div>
          </dl>
        </section>

        {details.stateRegistrations && details.stateRegistrations.length > 0 && (
          <section className="card">
            <h2>Inscrições estaduais ({details.stateRegistrations.length})</h2>
            <div className="partner-list">
              {details.stateRegistrations.map((registration) => (
                <div className="partner-row" key={`${registration.state}-${registration.number}`}>
                  <span className="partner-info">
                    <span className="partner-name mono">{registration.number}</span>
                    <span className="partner-meta"><span>{registration.state}</span></span>
                  </span>
                  <span className={`badge ${registration.active ? "ok" : "bad"}`}>
                    {registration.active ? "Habilitada" : "Não habilitada"}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {details.sanctions && details.sanctions.length > 0 && (
          <section className="card span-2">
            <h2>Sanções públicas ({details.sanctions.length})</h2>
            <p className="card-hint">
              Cadastros CEIS e CNEP, da Controladoria-Geral da União.
            </p>
            <div className="partner-list">
              {details.sanctions.map((sanction, index) => (
                <div className="partner-row" key={`${sanction.type}-${index}`}>
                  <span aria-hidden><IconAlert size={17} /></span>
                  <span className="partner-info">
                    <span className="partner-name">{sanction.type}</span>
                    <span className="partner-meta">
                      <span>{sanction.source}</span>
                      {sanction.agency && <span>{sanction.agency}</span>}
                      {sanction.startedAt && <span>de {sanction.startedAt}</span>}
                      {sanction.endsAt && <span>até {sanction.endsAt}</span>}
                    </span>
                    {sanction.reason && <span className="partner-meta"><span>{sanction.reason}</span></span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="card">
          <h2>Atividades (CNAE)</h2>
          {details.mainActivity && (details.mainActivity.code || details.mainActivity.description) ? (
            <div className="cnae-item">
              <span className="cnae-code mono">{details.mainActivity.code}</span>
              <span>
                <strong>{sentenceCase(details.mainActivity.description) || "Descrição não publicada pela fonte"}</strong>{" "}
                <span className="badge accent" style={{ marginLeft: 4 }}>Principal</span>
              </span>
            </div>
          ) : (
            <p style={{ color: "var(--text-muted)" }}>Não informado.</p>
          )}
          {activitiesList.shown.map((activity) => (
            <div className="cnae-item" key={activity.code + activity.description}>
              <span className="cnae-code mono">{activity.code}</span>
              <span>{sentenceCase(activity.description)}</span>
            </div>
          ))}
          <MoreButton list={activitiesList} noun="atividades" />

        </section>

        {details.establishments.length > 1 && (
          <section className="card span-2">
            <h2>Matriz e filiais ({details.establishments.length})</h2>
            <div className="branch-list">
              {branchesList.shown.map((establishment) => (
                <div className="partner-row" key={establishment.cnpj}>
                  <span aria-hidden><IconBuilding size={17} /></span>
                  <span className="partner-info">
                    <span className="partner-name">
                      {establishment.tradeName || details.corporateName}{" "}
                      <span className="badge plain">{establishment.type}</span>
                    </span>
                    <span className="partner-meta">
                      <span className="mono">{establishment.cnpj}</span>
                      <span>{[establishment.city, establishment.state].filter(Boolean).join(" / ")}</span>
                      {establishment.openedAt && <span>Desde {establishment.openedAt}</span>}
                    </span>
                  </span>
                  <StatusBadge status={establishment.status} />
                </div>
              ))}
              <MoreButton list={branchesList} noun="unidades" />
            </div>
          </section>
        )}
        {timeline.length > 0 && (
          <section className="card">
            <h2>Linha do tempo</h2>
            <ol className="timeline">
              {eventsList.shown.map((event, index) => (
                <li className="timeline-item" key={`${event.date}-${event.title}-${index}`}>
                  <span className="timeline-dot" aria-hidden />
                  <div className="timeline-body">
                    <span className="timeline-date mono">{event.date}</span>
                    <span className="timeline-title">{event.title}</span>
                    {event.detail && <span className="timeline-detail">{event.detail}</span>}
                  </div>
                </li>
              ))}
            </ol>
            <MoreButton list={eventsList} noun="eventos" />
          </section>
        )}

        <section className="card teia-banner">
          <span className="teia-banner-mark" aria-hidden><IconTeiaMark size={30} /></span>
          <h2 className="teia-banner-title">Quem está por trás deste CNPJ?</h2>
          <p className="teia-banner-text">
            Abra a teia e veja sócios, participações e as empresas ligadas a elas, em um grafo
            que você expande com um clique.
          </p>
          <button className="btn btn-primary" onClick={() => navigate({ page: "network", cnpj })}>
            <IconWeb size={16} /> Abrir teia societária
          </button>
        </section>

        {mapQuery && (
          <section className="card map-card">
            <h2>Localização</h2>
            <div className="map-frame">
              <iframe
                title={`Mapa de ${details.corporateName}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed&z=16`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              className="map-open"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Abrir no Google Maps
            </a>
          </section>
        )}
      </div>

    </main>
  );
}
